import React, { useEffect, useState, createContext, useContext, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { signOut } from "aws-amplify/auth";
import { Sidebar } from "./components/Sidebar/Sidebar";
import * as S from "./App.styles";
import Settings from "./components/Settings/Settings";

import { ResearchTablesV2 } from "./components/ResearchTable/ResearchTablesV2";



import { OnboardingGuard } from "./components/Onboarding";
import { billingService, BillingInfo } from "./services/billingService";
import { userService } from "./services/userService";
import { ProjectProvider, useProject } from "./contexts/ProjectContext";
import { ShopifyProvider } from "./contexts/ShopifyContext";
import { ShopifyConnectionHandler } from "./components/ShopifyConnectionHandler";
import { ProjectHeader } from "./common/ProjectHeader";
import { researchTableService, type ResearchTable } from "./services/researchTableService";
import { navigationService } from "./services/navigationService";
import { LibraryExtension } from "./components/Sidebar/LibraryExtension";
import { OnboardingDebugToggle } from "./common/OnboardingDebugToggle";
import { ManagerChat } from "./components/ManagerChat/ManagerChat";
import { ManagerSelection } from "./components/ManagerChat/ManagerSelection";




// Smart redirect component for root route
const RootRedirect: React.FC = () => {
  const { projects } = useProject();
  
  // If we have projects, redirect to the first project
  if (projects && projects.length > 0) {
    return <Navigate to={navigationService.project(projects[0].project_uuid)} replace />;
  }
  
  // If no projects, show simple empty state
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '2rem'
    }}>
      <h2 style={{ 
        margin: '0 0 0.5rem 0', 
        fontSize: '18px', 
        color: '#222429',
        fontWeight: '500'
      }}>
        No projects yet
      </h2>
      <p style={{ 
        margin: '0', 
        color: '#6b7280', 
        fontSize: '14px' 
      }}>
        Create your first project to get started
      </p>
    </div>
  );
};

// Trial Expired Modal - REMOVED (no trial restrictions)

// Auth Context for logout functionality
interface AuthContextType {
  handleLogout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [librarySidebarOpen, setLibrarySidebarOpen] = useState(false);
  const [mainSidebarExpanded, setMainSidebarExpanded] = useState(false);
  const [libraryExtensionExpanded, setLibraryExtensionExpanded] = useState(false);
  const [creatorExtensionOpen, setCreatorExtensionOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState({
    name: 'Loading...',
    organization: 'Loading...',
    avatar: 'L'
  });

  // Global stages/tables state to prevent header re-renders
  const [tables, setTables] = useState<ResearchTable[]>([]);
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  const [tablesLoading, setTablesLoading] = useState(false);
  
  // Table action triggers - create refs to communicate with ResearchTable
  const [exportTrigger, setExportTrigger] = useState<number>(0);
  const [copyTrigger, setCopyTrigger] = useState<number>(0);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [runAllTrigger, setRunAllTrigger] = useState<number>(0);
  const [runUnrunTrigger, setRunUnrunTrigger] = useState<number>(0);
  const [analyzeOutreachTrigger, setAnalyzeOutreachTrigger] = useState<{ templateId: string; timestamp: number } | null>(null);
  
  // Get project context
  const { selectedProject, projects } = useProject();
  
  // Determine if current route should show project header
  const shouldShowProjectHeader = () => {
    // Show on project routes
    if (location.pathname.startsWith('/projects/')) {
      return true;
    }
    // Show on root route when no projects exist (for project creation)
    if (location.pathname === '/' && (!projects || projects.length === 0)) {
      return true;
    }
    // Don't show on global routes (settings, etc.)
    return false;
  };

  // Listen for global library extension events
  React.useEffect(() => {
    const handleOpenCreatorLibrary = () => {
      setCreatorExtensionOpen(true);
    };

    const handleCloseLibraryExtensions = () => {
      setCreatorExtensionOpen(false);
    };

    window.addEventListener('openCreatorLibrary', handleOpenCreatorLibrary);
    window.addEventListener('closeLibraryExtensions', handleCloseLibraryExtensions);

    return () => {
      window.removeEventListener('openCreatorLibrary', handleOpenCreatorLibrary);
      window.removeEventListener('closeLibraryExtensions', handleCloseLibraryExtensions);
    };
  }, []);




  // Listen for custom library sidebar toggle events
  useEffect(() => {
    const handleToggleLibrarySidebar = () => {
      setLibrarySidebarOpen(prev => !prev);
    };

    window.addEventListener('toggleLibrarySidebar', handleToggleLibrarySidebar);
    return () => {
      window.removeEventListener('toggleLibrarySidebar', handleToggleLibrarySidebar);
    };
  }, []);

  // Handle Shopify embedded app with session tokens (modern approach)
  useEffect(() => {
    const handleShopifyEmbedded = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      const host = urlParams.get('host');
      
      // Skip if no shop parameter (not a Shopify context)
      if (!shop) {
        return;
      }
      
      // Validate shop domain format
      const shopRegex = /^[a-z0-9][a-z0-9\-]*\.myshopify\.com$/i;
      if (!shopRegex.test(shop)) {
        console.error('[Shopify] Invalid shop domain:', shop);
        return;
      }
      
      console.log('[Shopify] Embedded app detected for shop:', shop);
      
      // The app will be loaded in Shopify admin iframe
      // Session token authentication happens automatically via App Bridge
      // Token exchange will happen when the app makes its first API call
      
      // Clean up URL to remove Shopify params after initial load
      if (host) {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }
    };
    
    handleShopifyEmbedded();
  }, []);

  // Load billing data and account info
  useEffect(() => {
    const loadBillingData = async () => {
      try {
        // If returned from Stripe add-on checkout, fulfill and then refresh
        const url = new URL(window.location.href);
        const sessionId = url.searchParams.get('session_id');
        if (sessionId) {
          await billingService.fulfillAiAddon(sessionId);
          url.searchParams.delete('session_id');
          window.history.replaceState({}, document.title, url.toString());
        }

        const billingData = await billingService.getCurrentSubscription();
        setBilling(billingData);
        console.log('[App] Loaded billing data:', billingData);
      } catch (error) {
        console.warn("[AppContent] Failed to load billing data:", error);
        // Don't block the app if billing fails
      }
    };

    const loadAccountInfo = async () => {
      try {
        const userData = await userService.getCurrentUser();
        
        if (userData) {
          // Generate initials from name or email
          let initials = 'UN';
          if (userData.name) {
            initials = userData.name
              .split(' ')
              .map(name => name.charAt(0).toUpperCase())
              .slice(0, 2)
              .join('');
          } else if (userData.email) {
            const prefix = userData.email.split('@')[0];
            initials = (prefix.charAt(0) + (prefix.charAt(1) || '')).toUpperCase();
          }
          
          setAccountInfo({
            name: userData.name || 'Unknown User',
            organization: userData.organization_name || 'No Organization',
            avatar: initials
          });
        } else {
          throw new Error('No user data');
        }
      } catch (error) {
        console.error('Error loading account info:', error);
        setAccountInfo({
          name: 'Error Loading',
          organization: 'Error Loading',
          avatar: 'ER'
        });
      }
    };

    loadBillingData();
    loadAccountInfo();
  }, []);



  // Move useEffect outside of conditionals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "=" || e.key === "-")) {
        e.preventDefault();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // Load tables when project changes - global state to prevent header re-renders
  useEffect(() => {
    const loadTables = async () => {
      if (!selectedProject) {
        setTables([]);
        setActiveTableId(null);
        return;
      }
      
      try {
        setTablesLoading(true);
        const projectTables = await researchTableService.getResearchTables(selectedProject.project_uuid);
        setTables(projectTables);
        
        // Set first table as active if none selected and no URL parameter
        const urlParams = new URLSearchParams(location.search);
        const tableParam = urlParams.get('table');
        
        if (tableParam && projectTables.find(t => t.id === tableParam)) {
          setActiveTableId(tableParam);
        } else if (projectTables.length > 0 && !activeTableId) {
          setActiveTableId(projectTables[0].id);
        }
      } catch (error) {
        console.error('Error loading tables:', error);
      } finally {
        setTablesLoading(false);
      }
    };

    loadTables();
  }, [selectedProject?.project_uuid]); // Only depend on project ID, not activeTableId

  // Sync activeTableId with URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tableParam = urlParams.get('table');
    if (tableParam && tables.find(t => t.id === tableParam) && activeTableId !== tableParam) {
      setActiveTableId(tableParam);
    }
  }, [location.search, tables, activeTableId]);

  const showBetaBanner = false; // true;

  const handleToggleLibrarySidebar = () => {
    setLibrarySidebarOpen(!librarySidebarOpen);
  };

  const handleMainSidebarExpandedChange = (expanded: boolean) => {
    setMainSidebarExpanded(expanded);
  };

  // Navigation handlers - using navigation service
  const handleStageClick = useCallback((tableId: string) => {
    setActiveTableId(tableId);
    if (selectedProject) {
      navigate(navigationService.projectWithTable(selectedProject.project_uuid, tableId));
    }
  }, [selectedProject, navigate]);


  const handleCreateStage = useCallback(() => {
    if (selectedProject) {
      navigate(navigationService.projectCreate(selectedProject.project_uuid));
    }
  }, [selectedProject, navigate]);

  const handleDeleteTable = useCallback(async (tableId: string) => {
    try {
      await researchTableService.deleteResearchTable(tableId);
      setTables(prev => prev.filter(t => t.id !== tableId));
      
      // Reset active table if deleted
      if (activeTableId === tableId) {
        const remainingTables = tables.filter(t => t.id !== tableId);
        setActiveTableId(remainingTables.length > 0 ? remainingTables[0].id : null);
      }
    } catch (error) {
      console.error('Error deleting table:', error);
    }
  }, [activeTableId, tables]);

  const handleExportCurrentTable = useCallback(() => {
    // Trigger export by incrementing the counter
    // ResearchTable component will listen to this change
    setExportTrigger(prev => prev + 1);
  }, []);

  const handleCopyCurrentTable = useCallback(() => {
    // Trigger copy by incrementing the counter
    setCopyTrigger(prev => prev + 1);
  }, []);

  const handleRefreshCurrentTable = useCallback(() => {
    // Trigger refresh by incrementing the counter
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleAnalyzeOutreach = useCallback((templateId: string, creators: any[]) => {
    console.log('🔍 [App] Analyze outreach triggered:', templateId, creators.length);
    setAnalyzeOutreachTrigger({ templateId, timestamp: Date.now() });
  }, []);

  const handleCreatorExtensionToggle = useCallback(() => {
    setCreatorExtensionOpen(prev => !prev);
  }, []);

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <S.AppWrapper>
      {/* Handle Shopify connection via session tokens */}
      <ShopifyConnectionHandler />
      
      {showBetaBanner && (
        <S.BetaSection>
          <S.BetaBanner>
            <S.BetaBadgeContainer>
              <S.BetaBadge>Beta</S.BetaBadge>
            </S.BetaBadgeContainer>
            <S.BetaMessage>
              This is an early version of Adly. Some features might not work as
              expected.
            </S.BetaMessage>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              
              
                <S.SupportLink href="mailto:gustav@adlystudios.com">
                <QuestionCircleOutlined />
                gustav@adlystudios.com
              </S.SupportLink>
            </div>
          </S.BetaBanner>
        </S.BetaSection>
      )}

        <S.AppInterface>
          <S.AppLayout>
            <div style={{ 
              flex: 1, 
              display: 'flex',
              minHeight: 0,
              maxWidth: '100%',
              width: '100%',
              overflow: 'hidden',
              minWidth: 0
            }}>
              <Sidebar
                onLogoClick={handleLogoClick}
                creatorExtensionOpen={creatorExtensionOpen}
                onCreatorExtensionToggle={handleCreatorExtensionToggle}
              />
              
              {/* Creator Library Extension */}
              <LibraryExtension 
                isOpen={creatorExtensionOpen}
                onClose={() => setCreatorExtensionOpen(false)}
                mainSidebarExpanded={false}
                mode="creators"
              />
              
              {/* Library Extension is now handled within Sidebar component */}



              {/* Main Content Area */}
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                minWidth: 0,
                maxWidth: '100%',
                width: '100%',
                overflow: 'hidden'
              }}>
                {/* Conditional Project Header - Only on project-related routes */}
                {shouldShowProjectHeader() && (
                  <ProjectHeader 
                    tables={tables}
                    activeTableId={activeTableId}
                    tablesLoading={tablesLoading}
                    onStageClick={handleStageClick}
                    onCreateStage={handleCreateStage}
                    onDeleteTable={handleDeleteTable}
                    onAnalyzeOutreach={handleAnalyzeOutreach}
                  />
                )}
                
                <Routes>
              {/* Smart root redirect */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* Settings */}
              <Route
                path="/settings"
                element={
                  <S.DirectRouteContainer>
                    <Settings />
                  </S.DirectRouteContainer>
                }
              />


              {/* Project Management */}
              <Route
                path="/projects/:projectId"
                element={
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', maxWidth: '100%', width: '100%', minWidth: 0 }} className="no-padding">
                    <ResearchTablesV2 
                      libraryExtensionExpanded={creatorExtensionOpen}
                      exportTrigger={exportTrigger}
                      copyTrigger={copyTrigger}
                      refreshTrigger={refreshTrigger}
                      runAllTrigger={runAllTrigger}
                      runUnrunTrigger={runUnrunTrigger}
                      analyzeOutreachTrigger={analyzeOutreachTrigger}
                    />
                  </div>
                }
              />
              

              {/* Fallback - redirect to root */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
              </div>
            </div>
          </S.AppLayout>
        </S.AppInterface>
        
        {/* Debug Toggle - Only shows in development */}
        {/* <OnboardingDebugToggle /> */}
    </S.AppWrapper>
  );
};

const App: React.FC = () => {
  const handleLogout = async () => {
    try {
      // Import userService dynamically to avoid circular dependencies
      const { userService } = await import('./services/userService');
      
      // Clear cached user data first
      userService.clearCache();
      
      await signOut({ global: true });
      // Let the auth state change trigger re-render
      window.location.href = "/";
    } catch (error) {
      console.error("Error signing out:", error);
      // Fallback to reload
      window.location.reload();
    }
  };

  const authValue = {
    handleLogout,
  };

  return (
    <Router>
      <ShopifyProvider>
        <AuthContext.Provider value={authValue}>
          <ProjectProvider>
            <OnboardingGuard>
              <AppContent />
            </OnboardingGuard>
          </ProjectProvider>
        </AuthContext.Provider>
      </ShopifyProvider>
    </Router>
  );
};

export default App;
